import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";
import { SLOT_OPTIONS } from "../constants/schedulingSlots";
import WindowTimePicker, { fmt12, toHms, toMin } from "../components/WindowTimePicker";
import { NetworkCalendarGrid, startOfWeek } from "../components/NetworkCalendarGrid";
import { ClinicMonthView } from "../components/ClinicMonthView";
import { mergeCalendarItems } from "../components/directBookings";
import { DirectBookingDetailModal } from "../components/DirectBookingDetailModal";
import { assignLocationColors, locationLabelMap } from "../components/locationColors";
import { tintMapFor, tintOf, assignLocationTints, STATUS_SHAPES } from "../constants/locationTints";
import {
  useLocationFilter, matchesLocationFilter, readLocationFilter, writeLocationFilter,
} from "../lib/clinicLocationFilter";
import { visibleProposedTimes } from "../components/visibleProposedTimes";
import { fmtRange, fmtMin12, rangeToMin, MAX_PROPOSED_TIMES } from "../components/proposalRange";
import { toast } from "sonner";
import {
  CalendarDays, Loader2, ChevronLeft, ChevronRight, RefreshCw,
  X, Clock, CheckCircle2, AlertTriangle, MessageSquare, Plus, ArrowLeft, Ban, RotateCcw, Info,
} from "lucide-react";

// ── Helpers ─────────────────────────────────────────────────────────────────
// Render a toast tailored to the Raintree sync status surfaced by the
// backend (raintree_synced / raintree_error). Keeps wording aligned with
// the same helper in NetworkScheduling.jsx.
function showSchedulingToast(baseMsg, data) {
  const synced = data?.raintree_synced;
  const err = data?.raintree_error;
  if (synced) {
    toast.success(`${baseMsg} · Raintree notified`);
    return;
  }
  if (!err) {
    toast.success(baseMsg);
    return;
  }
  const errStr = String(err);
  if (/timeout|connect|status code|n8n|http/i.test(errStr)) {
    toast.warning(`${baseMsg} · Raintree sync pending (saved locally)`);
  } else {
    toast.warning(`${baseMsg} · Raintree sync failed: ${errStr}`);
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtTime(hms) {
  if (!hms) return "";
  const [h, m] = hms.split(":");
  const hr = parseInt(h, 10);
  return `${((hr + 11) % 12) + 1}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}
function formatApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return JSON.stringify(detail);
}

const STATUS_META = {
  pending: { label: "Pending", ring: "bg-amber-100 text-amber-900 border-amber-300" },
  suggested_alternative: { label: "Alt Sent", ring: "bg-orange-100 text-orange-900 border-orange-300" },
  locked_in: { label: "Confirmed", ring: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

// ── Suggest Alternative modal ───────────────────────────────────────────────
function SuggestAlternativeModal({ open, request, onClose, onDone }) {
  const [times, setTimes] = useState([{ date: "", start_time: "" }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setTimes([{ date: "", start_time: "" }]);
      setNotes("");
      setErr("");
    }
  }, [open]);

  if (!open || !request) return null;

  const addRow = () => times.length < 3 && setTimes([...times, { date: "", start_time: "" }]);
  const removeRow = (i) => setTimes(times.filter((_, idx) => idx !== i));
  const update = (i, f, v) => { const n = [...times]; n[i] = { ...n[i], [f]: v }; setTimes(n); };
  const canSubmit = times.length > 0 && times.every(t => t.date && t.start_time);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/scheduling/clinic/requests/${request.id}/suggest-alternative`, {
        proposed_times: times.map(t => ({ date: t.date, start_time: t.start_time })),
        notes: notes || null,
      });
      onDone?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Failed to send alternatives"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/50 p-4" data-testid="suggest-modal">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Suggest Alternative Times</h2>
            <p className="text-xs text-slate-500 mt-0.5">{request.patient_name} · {request.appointment_type}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="suggest-modal-close"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-slate-700">Your proposed times (1–{MAX_PROPOSED_TIMES}, 15 min each)</label>
              <button type="button" onClick={addRow} disabled={times.length >= MAX_PROPOSED_TIMES} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40 inline-flex items-center gap-1" data-testid="suggest-add-time">
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {times.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`suggest-time-row-${i}`}>
                  <input type="date" value={t.date} onChange={e => update(i, "date", e.target.value)} className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid={`suggest-date-${i}`} />
                  <select value={t.start_time} onChange={e => update(i, "start_time", e.target.value)} className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 bg-white" data-testid={`suggest-start-${i}`}>
                    <option value="">Select time…</option>
                    {SLOT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {times.length > 1 && (
                    <button type="button" onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600 p-2"><X size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes for WestStar (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. Therapist only available afternoons this week" className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" data-testid="suggest-notes" />
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="suggest-error">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={!canSubmit || busy} className="px-4 py-2 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2" data-testid="suggest-submit-btn">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Send Alternatives
          </button>
        </div>
      </div>
    </div>
  );
}

// "YYYY-MM-DDTHH:MM" for right now in Pacific — the clinic/portal frame that
// every appointment datetime in this app is stored and rendered in. Lexical
// comparison of these stamps is safe and timezone-independent.
export function pacificNowStamp(now = new Date()) {
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(now).reduce((acc, x) => ({ ...acc, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day}T${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
}

// ── Cancel Appointment modal (clinic) ────────────────────────────────────────
// Required reason field — clinic users MUST provide a reason because it
// gets surfaced in the CRM Queue patient notes for WestStar staff.
function CancelAppointmentModal({ open, request, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setErr("");
    }
  }, [open]);

  if (!open || !request) return null;

  const confirmedTime = (request.proposed_times || []).find((p) => p.is_confirmed) || null;
  const trimmed = reason.trim();
  const canSubmit = trimmed.length > 0 && !busy;

  const submit = async () => {
    if (!trimmed) return;
    setBusy(true);
    setErr("");
    try {
      const res = await api.put(`/scheduling/clinic/requests/${request.id}/cancel`, { reason: trimmed });
      const pname = request.patient_name || `Patient #${request.patient_id}`;
      // Never swallow the webhook's outcome: synced / skipped / failed all
      // read differently to the clinic.
      if (res.data?.raintree_skipped) {
        toast.warning(`Appointment cancelled for ${pname} · this appointment had no Raintree booking to cancel`);
      } else {
        showSchedulingToast(`Appointment cancelled for ${pname}`, res.data);
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Cancellation failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="clinic-cancel-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/60 p-4"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
            <AlertTriangle size={18} className="text-rose-600" />
            Cancel Appointment?
          </h3>
          <button
            data-testid="clinic-cancel-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-slate-700">
            You're about to cancel the appointment for{" "}
            <strong>{request.patient_name || `Patient #${request.patient_id}`}</strong>
            {confirmedTime && (
              <>
                {" "}on{" "}
                <strong>
                  {new Date(`${confirmedTime.date}T00:00:00`).toLocaleDateString("en-US", {
                    weekday: "short", month: "short", day: "numeric", year: "numeric",
                  })}
                </strong>{" "}
                at <strong>{fmtTime(confirmedTime.start_time)}</strong>
              </>
            )}
            . WestStar will see this reason in the patient's CRM record.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Reason for cancellation <span className="text-rose-600">*</span>
            </label>
            <textarea
              data-testid="clinic-cancel-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              placeholder="e.g. Therapist out sick, equipment unavailable, patient requested reschedule"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-rose-500 focus:ring-2 focus:ring-rose-100"
            />
            {!trimmed && (
              <p data-testid="clinic-cancel-reason-hint" className="text-[11px] text-slate-500 mt-1">
                A reason is required so WestStar knows why the appointment was cancelled.
              </p>
            )}
          </div>
          {err && (
            <div data-testid="clinic-cancel-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
          >
            Keep appointment
          </button>
          <button
            type="button"
            data-testid="clinic-cancel-submit"
            disabled={!canSubmit}
            onClick={submit}
            className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Yes, cancel
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Reschedule Appointment modal (clinic) ────────────────────────────────────
// 2026-08-31: this is now the DIRECT lane — one new date + time, booked
// immediately via /clinic/requests/{id}/reschedule-direct (cancel the old,
// book the new, stage back to Network Confirmed). Network clinics manage
// their own calendars, so WestStar's review step is traded for after-the-fact
// visibility (audit row + the appointment appearing in the CRM at once).
// The older propose-times-to-WestStar lane (`/reschedule`, stage 14) is
// untouched server-side and still serves in-flight requests.
function RescheduleAppointmentModal({ open, request, onClose, onDone }) {
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setDate("");
      setStartTime("");
      setReason("");
      setErr("");
    }
  }, [open]);

  if (!open || !request) return null;

  const confirmedTime = (request.proposed_times || []).find((p) => p.is_confirmed) || null;
  const trimmedReason = reason.trim();
  // Compare in the SAME frame the server uses (`_require_future_appointment`
  // → Pacific wall clock). `new Date(...)` would use the browser's zone, so
  // a clinic laptop set to another timezone could block a valid slot or
  // offer one the server then 400s.
  const isFuture = !!date && !!startTime
    && `${date}T${startTime.length === 5 ? startTime : startTime.slice(0, 5)}` > pacificNowStamp();
  const canSubmit = trimmedReason.length > 0 && !!date && !!startTime && isFuture && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const res = await api.put(`/scheduling/clinic/requests/${request.id}/reschedule-direct`, {
        reason: trimmedReason,
        date,
        start_time: startTime,
      });
      const pname = request?.patient_name || `Patient #${request?.patient_id ?? ""}`;
      showSchedulingToast(`Appointment moved for ${pname}`, res.data);
      onDone?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Reschedule failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="clinic-reschedule-modal"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/60 p-4"
    >
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <RotateCcw size={18} className="text-amber-600" />
              Reschedule Appointment
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {request.patient_name || `Patient #${request.patient_id}`} · {request.appointment_type}
            </p>
          </div>
          <button
            data-testid="clinic-reschedule-close"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={20} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {confirmedTime && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700">
              <span className="text-xs text-slate-500 uppercase tracking-wide mr-1.5">Cancelling</span>
              {new Date(`${confirmedTime.date}T00:00:00`).toLocaleDateString("en-US", {
                weekday: "short", month: "short", day: "numeric", year: "numeric",
              })}{" "}
              at {fmtTime(confirmedTime.start_time)}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Reason for rescheduling <span className="text-rose-600">*</span>
            </label>
            <textarea
              data-testid="clinic-reschedule-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              autoFocus
              placeholder="e.g. Therapist switched shifts; only available afternoons that week"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              New date and time <span className="text-rose-600">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="clinic-reschedule-date"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
              />
              <select
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="clinic-reschedule-start"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-300 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 bg-white"
              >
                <option value="">Select time…</option>
                {SLOT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              The old appointment is cancelled and this new one is booked right away.
            </p>
            {date && startTime && !isFuture && (
              <p data-testid="clinic-reschedule-past-hint" className="text-[11px] text-rose-600 mt-1">
                Pick a date and time in the future.
              </p>
            )}
          </div>
          {err && (
            <div data-testid="clinic-reschedule-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">
            Keep appointment
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="clinic-reschedule-submit"
            className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            <RotateCcw size={14} /> Move Appointment
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Request detail (read-mostly, with Confirm/Suggest actions) ──────────────
export function ClinicRequestDetailModal({ open, request, onClose, onConfirmed, onSuggested, profile = null }) {
  const [selected, setSelected] = useState(null);
  // C.1/C.3 — the shared picker's state. `pickMin` is the chosen start in
  // minutes; null means "window start", which keeps one-click confirm
  // identical to pre-ticket behaviour.
  const [pickMin, setPickMin] = useState(null);
  const [booked, setBooked] = useState([]);
  const [bookedLoading, setBookedLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setPickMin(null);
      setBooked([]);
      setErr("");
    }
  }, [open, request?.id]);

  if (!open || !request) return null;
  // `!== "network"` rather than `=== "weststar"`: a patient-submitted
  // window (Contract §11 Commit 3) is our side of the conversation and the
  // clinic confirms it identically — filtering on "weststar" would have
  // hidden patient availability from the clinic entirely.
  const wsTimes = (request.proposed_times || []).filter(p => p.source !== "network");
  const netTimes = (request.proposed_times || []).filter(p => p.source === "network");
  const isPending = request.status === "pending";
  const isConfirmed = request.status === "locked_in";
  // 2026-08-31: past appointments are INFO-ONLY. Boundary: an appointment
  // whose start time has passed is past; one later today stays actionable.
  // The server enforces the same rule (`_require_future_appointment`).
  const confirmedSlot = (request.proposed_times || []).find(p => p.is_confirmed) || null;
  const isPastAppt = !!confirmedSlot && new Date(
    `${confirmedSlot.date}T${(confirmedSlot.start_time || "00:00:00").slice(0, 8)}`
  ) <= new Date();

  const locMeta = (profile?.locations || []).find(
    (l) => l.location_code === request?.location_code) || null;
  const locLabel = locMeta?.location_name || request?.location_code || "";
  // B.4/B.6 — the roster `name` (server `short_label`) is the identity
  // label; the tint is the same hue the calendar block carries.
  const locShort = locMeta?.short_label || locLabel;
  const locTint = tintOf(tintMapFor(profile), request?.location_code);
  const duration = locMeta?.default_eval_duration_minutes
    || profile?.default_eval_duration_minutes || 15;
  const selWin = (request?.proposed_times || []).find((p) => p.id === selected) || null;
  const startMin = selWin ? (pickMin ?? toMin(selWin.start_time)) : null;

  // C.3 — hints come from the SAME endpoint the calendar draws from, so a
  // chip warning and a calendar block can never disagree, and the clinic's
  // own location scoping applies unchanged. Lazy: one fetch per window
  // EXPAND (not per chip, not on modal open), and fail-open — a slow or
  // failing fetch renders chips unwarned rather than blocking the flow.
  const expandWindow = async (p) => {
    setSelected(p.id);
    setPickMin(null);
    setBooked([]);
    if (!p.date) return;
    setBookedLoading(true);
    try {
      const res = await api.get("/scheduling/clinic/calendar", {
        params: { start_date: p.date, end_date: p.date },
      });
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setBooked(items
        .filter((it) => (it.location_code || "") === (request.location_code || "")
          && ["locked_in", "direct", "confirmed", "reserved_blocked"].includes(it.status || it.kind))
        .flatMap((it) => (it.proposed_times || [{ start_time: it.start_time, end_time: it.end_time }])
          .filter((t) => (t.date || it.date) === p.date && (t.is_confirmed !== false))
          .map((t) => ({ start_time: t.start_time, end_time: t.end_time,
                         label: it.patient_name || it.label || "booked" }))));
    } catch (e) {
      console.warn("conflict hints unavailable (advisory only)", e);
    } finally {
      setBookedLoading(false);
    }
  };

  const confirm = async () => {
    if (!selected) return;
    setBusy(true); setErr("");
    try {
      // C.6 — the chosen start, as the clinic's wall clock. No offset is
      // sent on purpose: the server derives placement from the LOCATION's
      // timezone, so the browser's clock can never move an appointment.
      const payload = { proposed_time_id: selected, duration_minutes: duration };
      if (selWin && startMin !== null) {
        payload.selected_start = `${selWin.date}T${toHms(startMin)}`;
      }
      const res = await api.put(`/scheduling/clinic/requests/${request.id}/confirm`, payload);
      const pname = request?.patient_name || `Patient #${request?.patient_id ?? ""}`;
      showSchedulingToast(`Appointment confirmed for ${pname}`, res.data);
      onConfirmed?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e, "Confirm failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 p-4" data-testid="clinic-detail-modal">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-900">{request.patient_name || `Patient #${request.patient_id}`}</h2>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium ${STATUS_META[request.status]?.ring || ""}`} data-testid="clinic-detail-status">
                  {STATUS_META[request.status]?.label || request.status}
                </span>
                {/* B.6 — which clinic am I acting for? Same hue, same
                    name as the calendar block and the legend chip. */}
                {locLabel && locTint && (
                  <span
                    data-testid="clinic-detail-location"
                    className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border font-medium"
                    style={{ backgroundColor: locTint.tint, color: locTint.text, borderColor: locTint.border }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: locTint.border }} />
                    {locShort}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{request.appointment_type} · Submitted {request.submitted_at ? new Date(request.submitted_at).toLocaleString() : ""}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="clinic-detail-close"><X size={20} /></button>
          </div>
          <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {request.notes && (
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1"><MessageSquare size={12} /> WestStar notes</div>
                <div className="text-sm text-slate-600 bg-slate-50 rounded px-3 py-2 border border-slate-200">{request.notes}</div>
              </div>
            )}

            <div>
              <div className="text-xs font-medium text-slate-700 mb-2">WestStar's proposed times</div>
              <div className="space-y-2" data-testid="ws-proposed-times">
                {wsTimes.length === 0 && <div className="text-xs text-slate-400">None.</div>}
                {wsTimes.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={!isPending}
                    onClick={() => expandWindow(p)}
                    data-testid={`ws-proposed-time-${p.id}`}
                    className={`w-full text-left flex items-center justify-between gap-2 px-3 py-2 rounded-lg border transition ${
                      p.is_confirmed
                        ? "bg-emerald-50 border-emerald-200"
                        : selected === p.id
                        ? "bg-blue-50 border-blue-400 ring-2 ring-blue-100"
                        : isPending
                        ? "bg-white border-slate-200 hover:border-slate-400 cursor-pointer"
                        : "bg-slate-50 border-slate-200 opacity-70 cursor-not-allowed"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={13} className="text-slate-400" />
                      <span className="font-medium text-slate-900">
                        {new Date(`${p.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </span>
                      <span className="text-slate-500">{fmtTime(p.start_time)} – {fmtTime(p.end_time)}</span>
                    </div>
                    {p.is_confirmed && <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={13} /> Confirmed</span>}
                  </button>
                ))}
                {/* C.1 — the selected window expands inline into start-time
                    chips. Same component the calendar's in-block click
                    uses; there is no second picker. */}
                {isPending && selWin && (
                  <WindowTimePicker
                    window={selWin}
                    durationMinutes={duration}
                    booked={booked}
                    bookedLoading={bookedLoading}
                    locationLabel={locLabel}
                    value={pickMin}
                    onChange={setPickMin}
                  />
                )}
              </div>
            </div>

            {netTimes.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-700 mb-2">Your suggested alternatives</div>
                <div className="space-y-2">
                  {netTimes.map(p => (
                    <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-orange-200 bg-orange-50 text-sm">
                      <Clock size={13} className="text-orange-400" />
                      <span className="font-medium text-slate-900">
                        {new Date(`${p.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      </span>
                      <span className="text-slate-500">{fmtTime(p.start_time)} – {fmtTime(p.end_time)}</span>
                      <span className="ml-auto text-[10px] uppercase font-semibold text-orange-700">Awaiting WS</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isConfirmed && (
              <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200 inline-flex items-center gap-1.5">
                <CheckCircle2 size={13} /> This appointment is locked in. The patient has been notified via CRM.
              </div>
            )}
            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid="clinic-detail-error"><AlertTriangle size={14} /> {err}</div>}
          </div>
          {isPending && selWin && startMin !== null && (
            <div className="px-6 pt-3 text-sm text-slate-700 border-t border-slate-100"
                 data-testid="clinic-confirm-summary">
              <strong className="text-slate-900">{request.patient_name || `Patient #${request.patient_id}`}</strong>
              {" · "}
              {new Date(`${selWin.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              {" · "}
              {fmt12(startMin)} – {fmt12(startMin + duration)}
              {" · "}
              <strong className="text-slate-900">{locLabel}</strong>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50">
            {isPending ? (
              <>
                <button onClick={() => setSuggestOpen(true)} className="px-4 py-2 text-sm font-medium text-orange-700 border border-orange-300 hover:bg-orange-50 rounded-lg inline-flex items-center gap-1.5" data-testid="open-suggest-btn">
                  Suggest Alternative
                </button>
                <button onClick={confirm} disabled={!selected || busy} className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-1.5" data-testid="clinic-confirm-btn">
                  {busy && <Loader2 size={14} className="animate-spin" />}
                  {selWin && startMin !== null
                    ? `Confirm ${fmt12(startMin)}${locLabel ? ` at ${locLabel}` : ""}`
                    : "Confirm Selected Time"}
                </button>
              </>
            ) : isConfirmed ? (
              <>
                {isPastAppt ? (
                  <p className="text-sm text-slate-600" data-testid="clinic-past-appt-note">
                    This appointment has already happened — contact WestStar if it needs changing.
                  </p>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setRescheduleOpen(true)}
                      data-testid="clinic-reschedule-appt-btn"
                      className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg inline-flex items-center gap-1.5"
                    >
                      <RotateCcw size={14} /> Reschedule
                    </button>
                    <button
                      onClick={() => setCancelOpen(true)}
                      data-testid="clinic-cancel-appt-btn"
                      className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg inline-flex items-center gap-1.5"
                    >
                      <Ban size={14} /> Cancel Appointment
                    </button>
                  </div>
                )}
                <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Close</button>
              </>
            ) : (
              <button onClick={onClose} className="ml-auto px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg">Close</button>
            )}
          </div>
        </div>
      </div>
      <SuggestAlternativeModal
        open={suggestOpen}
        request={request}
        onClose={() => setSuggestOpen(false)}
        onDone={() => { setSuggestOpen(false); onSuggested?.(); onClose?.(); }}
      />
      <CancelAppointmentModal
        open={cancelOpen}
        request={request}
        onClose={() => setCancelOpen(false)}
        onDone={() => { setCancelOpen(false); onConfirmed?.(); onClose?.(); }}
      />
      <RescheduleAppointmentModal
        open={rescheduleOpen}
        request={request}
        onClose={() => setRescheduleOpen(false)}
        onDone={() => { setRescheduleOpen(false); onSuggested?.(); onClose?.(); }}
      />
    </>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
// ── Spotlight banner detail helpers (2026-06) ────────────────────────────────
// Missing values render as "—" — never blank-collapse, never "null".
const fmtMDY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("T")[0].split("-");
  return m && d && y ? `${m}/${d}/${y}` : "—";
};
const detailText = (v) => (v && String(v).trim()) || "—";
const joinBodyParts = (v) => {
  const parts = String(v || "").split(",").map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
};

// A.1 — "on the books" = work the clinic has actually committed to, which
// must stay visible in booking mode so a scheduler can't double-book.
// Proposals and counter-proposals are negotiation, not commitments.
const isOnTheBooks = (r) =>
  r?.status === "locked_in" || r?.status === "direct_booking" || r?.type === "direct_booking";

export default function ClinicScheduling() {  const { refreshPending, profile } = useOutletContext() || {};
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  // Month view (2026-08, replaces List): anchor = first of rendered month.
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [activeRequest, setActiveRequest] = useState(null);
  // View toggle — Month (full-month grid, default) | Calendar (the shared
  // read-only 7am–7pm week grid, mechanics unchanged).
  const [viewMode, setViewMode] = useState("month");
  // Set by calendar block click → switch to List and highlight that card.
  const [highlightRequestId, setHighlightRequestId] = useState(null);
  // ── Spotlight (2026-06): choose-a-time mode for ONE pending request.
  // View-state only — nothing persisted, no new fetches. ONE mechanism, two
  // entrances: the pill/card click AND the left-nav patient toggle both call
  // activateSpotlight — no parallel implementations.
  const [spotlight, setSpotlight] = useState(null); // { id, patient_name, windows, dates }
  const spotlightRef = useRef(null);
  useEffect(() => { spotlightRef.current = spotlight?.id ?? null; }, [spotlight]);
  // In-block slot pick → confirm dialog state
  const [slotPick, setSlotPick] = useState(null); // { request, time, date, slot_start }
  // Reserved WestStar slots (Phase 2, 2026-02) — instances for the visible
  // range; block/unblock confirms handled here, claim is internal-only.
  const [reservedSlots, setReservedSlots] = useState([]);
  const [reservedPending, setReservedPending] = useState(null); // { instance, action:'block'|'unblock' }
  // Paint mode (2026-02 phase 2): "WestStar Reserve" toggle. Pending
  // marks are page-owned lists of {date, slot_time}; Done commits via
  // /paint, Cancel discards, week navigation auto-commits with a
  // confirm if marks are present.
  const [paintMode, setPaintMode] = useState(false);
  const [paintAdds, setPaintAdds] = useState([]);       // [{date, slot_time}]
  const [paintRemoves, setPaintRemoves] = useState([]); // [{date, slot_time}]
  const [paintSaving, setPaintSaving] = useState(false);
  const [slotBusy, setSlotBusy] = useState(false);
  // Nav-order pending list, broadcast by ClinicPortal's existing pending
  // fetch (no new endpoint) — used to advance the spotlight after confirm.
  const [navPending, setNavPending] = useState([]);
  // A.1 — "Show other pending requests": session-remembered, OFF by
  // default, and it governs ONLY what booking mode layers back in.
  const SHOW_OTHERS_KEY = "clinic-booking-show-others";
  const [showOtherPending, setShowOtherPending] = useState(
    () => sessionStorage.getItem(SHOW_OTHERS_KEY) === "1");
  const toggleOtherPending = () => setShowOtherPending((v) => {
    const next = !v;
    sessionStorage.setItem(SHOW_OTHERS_KEY, next ? "1" : "0");
    return next;
  });
  // A.4 — the shared browse filter as it was BEFORE booking mode took it
  // over, so exiting restores exactly what the scheduler was looking at.
  const filterSnapshot = useRef(undefined);

  const activateSpotlight = useCallback((req) => {
    const times = visibleProposedTimes(req);
    const dates = [...new Set(times.map((t) => t.date))].sort();
    // Multi-week ranges: land on the EARLIEST week
    if (dates.length) setWeekStart(startOfWeek(new Date(`${dates[0]}T00:00:00`)));
    setViewMode("calendar");
    setHighlightRequestId(null);
    // A.4/A.5 — booking mode TAKES OVER the shared browse filter so the
    // table, the sidebar and the badge follow the booking location; the
    // previous value is kept for the restore on exit.
    if (filterSnapshot.current === undefined) filterSnapshot.current = readLocationFilter();
    if (req.location_code) writeLocationFilter([req.location_code]);
    setSpotlight({
      id: req.id,
      patient_name: req.patient_name || `Patient #${req.patient_id}`,
      location_code: req.location_code || null,
      // Scheduling details from the clinic-facing serializer — the banner
      // reads this shared state, so BOTH entrances (card click and nav
      // toggle) render identically for free.
      details: req.patient_details || null,
      windows: times.length,
      dates,
    });
  }, []);
  const exitSpotlight = useCallback(() => {
    setSpotlight(null);
    if (filterSnapshot.current !== undefined) {
      writeLocationFilter(filterSnapshot.current);
      filterSnapshot.current = undefined;
    }
  }, []);

  // Broadcast spotlight state so the left-nav marks the active entry
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("clinic-spotlight-state", { detail: { id: spotlight?.id ?? null } }));
  }, [spotlight]);

  // Entrance 2: left-nav toggles (event when mounted, sessionStorage handoff
  // when arriving from another clinic page). Clicking the active entry exits.
  useEffect(() => {
    const handler = (e) => {
      const req = e.detail?.request;
      if (!req) return;
      sessionStorage.removeItem("clinic-spotlight-request");
      if (spotlightRef.current === req.id) exitSpotlight(); // toggle off
      else activateSpotlight(req); // switch in place / enter
    };
    window.addEventListener("clinic-spotlight-request", handler);
    return () => window.removeEventListener("clinic-spotlight-request", handler);
  }, [activateSpotlight, exitSpotlight]);
  useEffect(() => {
    const raw = sessionStorage.getItem("clinic-spotlight-request");
    if (raw) {
      sessionStorage.removeItem("clinic-spotlight-request");
      try { activateSpotlight(JSON.parse(raw)); } catch { /* ignore bad payload */ }
    }
  }, [activateSpotlight]);
  useEffect(() => {
    const handler = (e) => setNavPending(e.detail?.requests || []);
    window.addEventListener("clinic-pending-list", handler);
    return () => window.removeEventListener("clinic-pending-list", handler);
  }, []);

  // Escape exits spotlight cleanly (back to default all-requests rendering)
  useEffect(() => {
    if (!spotlight) return undefined;
    const onKey = (e) => { if (e.key === "Escape") exitSpotlight(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [spotlight, exitSpotlight]);

  // Confirm the picked slot via the EXISTING confirm endpoint (server
  // validates the slot lies inside the range + on the 15-min grid), then
  // advance the spotlight to the next patient in nav order, else exit.
  // The clinic's booking duration for this request's location (C.2).
  const pickDuration = useCallback((req) => {
    const meta = (profile?.locations || []).find(
      (l) => l.location_code === req?.location_code);
    return meta?.default_eval_duration_minutes
      || profile?.default_eval_duration_minutes || 15;
  }, [profile]);

  const confirmSlot = async () => {
    if (!slotPick) return;
    setSlotBusy(true);
    try {
      const res = await api.put(`/scheduling/clinic/requests/${slotPick.request.id}/confirm`, {
        proposed_time_id: slotPick.time.id,
        // C.6: wall clock only — the server places it in the LOCATION's
        // timezone. `slot_start` stays for older clients.
        selected_start: `${slotPick.date}T${slotPick.slot_start.length === 5 ? slotPick.slot_start + ":00" : slotPick.slot_start}`,
        duration_minutes: pickDuration(slotPick.request),
        slot_start: slotPick.slot_start,
      });
      showSchedulingToast("Appointment confirmed", res.data);
      const confirmedId = slotPick.request.id;
      setSlotPick(null);
      onChanged();
      if (spotlight?.id === confirmedId) {
        const idx = navPending.findIndex((r) => r.id === confirmedId);
        const remaining = navPending.filter((r) => r.id !== confirmedId);
        const next = idx >= 0 ? remaining[idx] || remaining[remaining.length - 1] : null;
        if (next) activateSpotlight(next);
        else exitSpotlight();
      }
    } catch (e) {
      toast.error(formatApiError(e, "Failed to confirm"));
    } finally {
      setSlotBusy(false);
    }
  };

  const hasNextWeekWindows = useMemo(() => {
    if (!spotlight?.dates?.length) return false;
    const weekEndISO = toISODate(addDays(weekStart, 6));
    return spotlight.dates.some((d) => d > weekEndISO);
  }, [spotlight, weekStart]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      // Month mode fetches the full 6-week grid range; Calendar keeps the
      // exact week range it always fetched.
      let startISO, endISO;
      if (viewMode === "month") {
        const gridStart = startOfWeek(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1));
        startISO = toISODate(gridStart);
        endISO = toISODate(addDays(gridStart, 41));
      } else {
        startISO = toISODate(weekStart);
        endISO = toISODate(addDays(weekStart, 6));
      }
      const res = await api.get("/scheduling/clinic/calendar", {
        params: { start_date: startISO, end_date: endISO },
      });
      setAllRequests(mergeCalendarItems(res.data));
      // Reserved slots — best-effort load in parallel-ish; a failure here
      // must NOT block the calendar (feature is additive).
      try {
        const codes = (profile?.locations || []).map((l) => l.location_code).filter(Boolean);
        if (codes.length > 0) {
          const rs = await api.get("/scheduling/reserved-slots/instances", {
            params: {
              location_codes: codes.join(","),
              start_date: startISO,
              end_date: endISO,
            },
          });
          setReservedSlots(rs.data?.instances || []);
        } else {
          setReservedSlots([]);
        }
      } catch (rsErr) {
        // Silent — the reserved-slots surface is additive; the toast noise
        // for a stale-cache hiccup isn't worth it. Existing calendar keeps
        // functioning.
        setReservedSlots([]);
      }
    } catch (e) {
      setErr(formatApiError(e, "Failed to load calendar"));
    } finally {
      setLoading(false);
    }
  }, [weekStart, monthAnchor, viewMode, profile]);

  useEffect(() => { load(); }, [load]);

  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, 6);
    return `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  }, [weekStart]);
  const monthLabel = useMemo(
    () => monthAnchor.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    [monthAnchor]
  );

  // ── Per-location color coding (2026-08, multi-location logins only).
  // Single-location users get NO legend, NO tint, NO labels — pixel-
  // equivalent to today (pinned). Deterministic: sorted codes → fixed palette.
  //
  // B.1 (2026-06): ONE hue source for every clinic scheduling surface.
  // `assignLocationColors` still provides the entry SHAPE (and its >12
  // wrap suffix), but each entry's swatch/stripe is overwritten with the
  // location's WCAG-checked tint hue, and `tint` is carried along so the
  // week grid and the month chips paint the same hue for the same clinic.
  const profileLocations = useMemo(() => profile?.locations || [], [profile]);
  const isMultiLocation = profileLocations.length > 1;
  const locationColors = useMemo(
    () => {
      const base = isMultiLocation ? assignLocationColors(profileLocations.map((l) => l.location_code)) : null;
      if (!base) return null;
      // HOTFIX 2026-09-18: the tint comes from the SORTED-RANK assignment
      // map, not from a hash of the code (which collided for pro-pt's nine
      // P_000 codes). Both maps rank the same deduped sorted set, so the
      // chip colour and the tint are the same location's identity.
      const tints = assignLocationTints(profileLocations.map((l) => l.location_code));
      const out = {};
      Object.entries(base).forEach(([code, entry]) => {
        const t = tints[code]?.tint;
        out[code] = t
          ? { ...entry, tint: t, suffix: tints[code].suffix || entry.suffix,
              color: { ...entry.color, swatch: t.border, stripe: t.border } }
          : entry;
      });
      return out;
    },
    [isMultiLocation, profileLocations]
  );
  const locationLabels = useMemo(
    () => (isMultiLocation ? locationLabelMap(profileLocations) : null),
    [isMultiLocation, profileLocations]
  );
  // The booking banner needs identity even for a single-location login
  // (A.5: the selector is fixed there, so the pill is the identity).
  const tintMap = useMemo(() => tintMapFor(profile), [profile]);
  const allLabels = useMemo(() => locationLabelMap(profileLocations), [profileLocations]);
  const bookingTint = tintOf(tintMap, spotlight?.location_code);

  // D.2 — the ONE shared session location filter. DISPLAY ONLY: the
  // calendar fetch above is untouched (no location parameter is sent, and
  // scope stays the server's `user_locations` rule).
  const { selected: locFilter, isAll: locFilterIsAll, toggle: toggleLocation, showAll: showAllLocations } =
    useLocationFilter();
  // A.1 — booking-mode layering. Selecting a patient leaves exactly their
  // windows plus that location's ON-THE-BOOKS work visible; every other
  // patient's Pending / Alt-Sent is not rendered at all unless the
  // scheduler asks for it. Display only — the fetch is unchanged.
  const bookingId = spotlight?.id ?? null;
  const requests = useMemo(() => {
    const scoped = allRequests.filter((r) => matchesLocationFilter(locFilter, r.location_code));
    if (!bookingId) return scoped;
    return scoped.filter(
      (r) => r.id === bookingId || isOnTheBooks(r) || showOtherPending);
  }, [allRequests, locFilter, bookingId, showOtherPending]);
  // The re-revealed extras are the ONLY blocks that dim (the filtered
  // working set renders at full strength so a Confirmed block can never be
  // washed out at the moment it matters).
  const dimmedRequestIds = useMemo(() => {
    if (!bookingId) return null;
    return requests
      .filter((r) => r.id !== bookingId && !isOnTheBooks(r))
      .map((r) => r.id);
  }, [requests, bookingId]);
  const visibleReserved = useMemo(
    () => reservedSlots.filter((s) => matchesLocationFilter(locFilter, s.location_code)),
    [reservedSlots, locFilter]
  );
  // A.2 — one jump chip per window date, in order.
  const jumpDates = spotlight?.dates || [];
  const weekEndISO = toISODate(addDays(weekStart, 6));

  const onChanged = () => {
    load();
    refreshPending?.();
    window.dispatchEvent(new CustomEvent("clinic-pending-refresh"));
  };

  // Calendar block click = navigation only: flip to Month, scroll the
  // request's chip into view, pulse a highlight ring. No actions.
  const handleBlockClick = (requestId) => {
    setViewMode("month");
    setHighlightRequestId(requestId);
  };

  // Reserved-slot interaction (clinic side): open → confirm-then-block,
  // blocked → confirm-then-unblock. State comes from the instance list
  // computed on the server, so a 409 or stale click just triggers a
  // reload — the source of truth is on the server.
  const handleReservedClick = (instance) => {
    if (instance.state === "open") setReservedPending({ instance, action: "block" });
    else if (instance.state === "blocked") setReservedPending({ instance, action: "unblock" });
  };

  const submitReservedAction = async () => {
    if (!reservedPending) return;
    const { instance, action } = reservedPending;
    try {
      await api.post(`/scheduling/reserved-slots/${action}`, {
        location_code: instance.location_code,
        date: instance.date,
        slot_time: instance.slot_time,
      });
      toast.success(action === "block" ? "Reserved slot marked as filled" : "Reserved slot reopened");
      setReservedPending(null);
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail || e?.message || `Failed to ${action}`;
      if (status === 409) {
        toast.warning(`${msg} · refreshing calendar`);
        setReservedPending(null);
        load();
      } else {
        toast.error(String(msg));
      }
    }
  };

  // ── Paint mode handlers ───────────────────────────────────────────
  // Identity for a pending mark is (date, slot_time). Toggle logic:
  //   • empty cell (existingState=null) → add to pending-adds, or if
  //     already in pending-adds, remove (undo).
  //   • open/blocked committed slot → add to pending-removes, or undo.
  //   • pending-add marker → undo (removes it from pending-adds).
  //   • booked slot → inert (grid enforces).
  //
  // Only ONE profile.location is painted at a time — the currently-
  // selected code (`activePaintLocation`); multi-location clinic
  // logins choose via a small toggle at the top of the calendar.
  const activePaintLocation = useMemo(() => {
    if (!profile?.locations || profile.locations.length === 0) return null;
    // Multi-location: reuse the location selector from the existing
    // scheduling page if there is one; otherwise default to the
    // primary. Kept simple: first location.
    return profile.locations[0].location_code;
  }, [profile]);

  const handlePaintCell = useCallback(({ iso, slot_time, existingState }) => {
    const key = (arr, iso_, t) => arr.findIndex((p) => p.date === iso_ && (p.slot_time.length === 5 ? `${p.slot_time}:00` : p.slot_time) === t);
    if (existingState === "pending-add") {
      // Undo pending-add
      setPaintAdds((prev) => {
        const i = key(prev, iso, slot_time);
        if (i < 0) return prev;
        const next = [...prev]; next.splice(i, 1); return next;
      });
      return;
    }
    if (existingState === null) {
      // Empty cell → toggle pending-add.
      setPaintAdds((prev) => {
        const i = key(prev, iso, slot_time);
        if (i >= 0) { const n = [...prev]; n.splice(i, 1); return n; }
        return [...prev, { date: iso, slot_time }];
      });
      return;
    }
    if (existingState === "open" || existingState === "blocked") {
      // Committed slot → toggle pending-remove.
      setPaintRemoves((prev) => {
        const i = key(prev, iso, slot_time);
        if (i >= 0) { const n = [...prev]; n.splice(i, 1); return n; }
        return [...prev, { date: iso, slot_time }];
      });
      return;
    }
    // booked (or anything else) → inert.
  }, []);

  const submitPaintBatch = useCallback(async ({ silent = false } = {}) => {
    if (paintAdds.length === 0 && paintRemoves.length === 0) return true;
    if (!activePaintLocation) return false;
    setPaintSaving(true);
    try {
      const res = await api.post("/scheduling/reserved-slots/paint", {
        location_code: activePaintLocation,
        additions: paintAdds,
        removals: paintRemoves,
      });
      const bs = res.data?.booked_skipped || [];
      if (bs.length > 0) {
        toast.warning(`${bs.length} slot(s) already booked — kept as-is`);
      }
      const added = res.data?.added ?? 0;
      const removed = res.data?.removed ?? 0;
      if (!silent) toast.success(`Reserved slots updated · +${added} / −${removed}`);
      setPaintAdds([]);
      setPaintRemoves([]);
      await load();
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to save reserved slots";
      toast.error(String(msg));
      return false;
    } finally {
      setPaintSaving(false);
    }
  }, [paintAdds, paintRemoves, activePaintLocation, load]);

  const cancelPaintBatch = () => {
    setPaintAdds([]);
    setPaintRemoves([]);
  };

  const exitPaintMode = () => {
    setPaintMode(false);
    cancelPaintBatch();
  };

  // Week navigation mid-mode → auto-commit with confirm if pending marks.
  const withPaintAutoCommit = async (navFn) => {
    if (!paintMode || (paintAdds.length === 0 && paintRemoves.length === 0)) {
      navFn();
      return;
    }
    if (!window.confirm(`Save ${paintAdds.length + paintRemoves.length} pending change(s) on this week before navigating?`)) {
      cancelPaintBatch();
      navFn();
      return;
    }
    const ok = await submitPaintBatch({ silent: true });
    if (ok) navFn();
  };

  useEffect(() => {
    if (viewMode !== "month" || !highlightRequestId) return;
    const el = document.querySelector(`[data-testid^="month-chip-${highlightRequestId}-"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightRequestId(null), 4000);
    return () => clearTimeout(t);
  }, [viewMode, highlightRequestId]);

  return (
    <div className="p-6 space-y-5" data-testid="clinic-scheduling-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays size={22} className="text-blue-600" />
            Scheduling Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Pending availability from WestStar and your confirmed appointments</p>
        </div>
        <div className="flex items-center gap-2">
          {viewMode === "calendar" && (
            <button
              type="button"
              data-testid="reserve-mode-toggle"
              onClick={() => paintMode ? exitPaintMode() : setPaintMode(true)}
              className={`px-3 py-2 text-sm font-semibold rounded-lg border ${
                paintMode
                  ? "bg-blue-600 text-white border-blue-700 hover:bg-blue-700"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {paintMode ? "Exit Reserve" : "WestStar Reserve"}
            </button>
          )}
          <div
            className="inline-flex rounded-lg border border-slate-300 overflow-hidden"
            data-testid="clinic-view-toggle"
          >
          <button
            type="button"
            data-testid="clinic-view-toggle-month"
            onClick={() => setViewMode("month")}
            className={`px-3 py-2 text-sm font-medium ${
              viewMode === "month"
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Month
          </button>
          <button
            type="button"
            data-testid="clinic-view-toggle-calendar"
            onClick={() => {
              setViewMode("calendar");
              setHighlightRequestId(null);
            }}
            className={`px-3 py-2 text-sm font-medium border-l border-slate-300 ${
              viewMode === "calendar"
                ? "bg-slate-800 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            Calendar
          </button>
        </div>
        </div>
      </div>

      {paintMode && (
        <div
          data-testid="reserve-mode-banner"
          className="bg-blue-50 border border-blue-300 rounded-lg px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="text-sm text-blue-900">
            <strong>Reserve mode:</strong> click 15-min slots to reserve for WestStar — click a reserved slot to remove it.
            <span className="text-blue-700"> · Pending: <span data-testid="paint-pending-count">{paintAdds.length + paintRemoves.length}</span></span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="paint-cancel"
              onClick={cancelPaintBatch}
              disabled={paintSaving || (paintAdds.length === 0 && paintRemoves.length === 0)}
              className="px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="button"
              data-testid="paint-done"
              onClick={() => submitPaintBatch()}
              disabled={paintSaving || (paintAdds.length === 0 && paintRemoves.length === 0)}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {paintSaving ? "Saving…" : "Done"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        {/* Per-location legend (multi-location logins ONLY — single-location
            users see no legend, no tint: pixel-equivalent, pinned).
            B.6 — the chips are the location FILTER: click to isolate a
            clinic, click again to bring everything back. Display only.
            A.5 — this IS the browse-mode location selector, wired to the
            one shared session filter. For a single-location login there is
            nothing to select, so the selector is fixed by construction
            (not rendered) and the filter stays "all" — the earlier
            pixel-equivalence ruling for single-location clinics wins over
            rendering a one-option control. */}
        {isMultiLocation && locationColors && (
          <div
            data-testid="location-legend"
            className="flex items-center gap-2 flex-wrap mb-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200"
          >
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Locations</span>
            <button
              type="button"
              data-testid="legend-all"
              aria-pressed={locFilterIsAll}
              onClick={showAllLocations}
              className={`text-xs px-2 py-1 rounded-full border transition ${
                locFilterIsAll
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
              }`}
            >
              All
            </button>
            {profileLocations.map((l) => {
              const entry = locationColors[l.location_code];
              const on = matchesLocationFilter(locFilter, l.location_code);
              return (
                <button
                  key={l.location_code}
                  type="button"
                  data-testid={`legend-${l.location_code}`}
                  aria-pressed={on}
                  onClick={() => toggleLocation(l.location_code)}
                  title={`Show only ${locationLabels?.[l.location_code] || l.location_code}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border transition ${
                    on
                      ? "bg-white text-slate-800 border-slate-400 shadow-sm"
                      : "bg-white text-slate-400 border-slate-200 opacity-60 hover:opacity-100"
                  }`}
                  style={on ? { backgroundColor: entry?.tint?.tint, color: entry?.tint?.text, borderColor: entry?.tint?.border } : undefined}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry?.color?.swatch }} />
                  {(locationLabels?.[l.location_code] || l.location_code)}{entry?.suffix || ""}
                </button>
              );
            })}
            {!locFilterIsAll && (
              <span data-testid="legend-filter-note" className="text-[11px] text-slate-500">
                Showing {locFilter.length} of {profileLocations.length} — filter affects this view only
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          {/* B.2 — status is SHAPE, never hue: the location owns the hue,
              so these swatches are neutral and differ only in shape.
              `legend-direct` keeps its pinned indigo chip (a direct
              booking is a KIND, not a negotiation status). */}
          <div className="flex items-center gap-4 text-[11px] text-slate-500" data-testid="status-shape-legend">
            <span className="inline-flex items-center gap-1.5" data-testid="legend-status-pending">
              <span className="w-3 h-3 rounded border border-dashed border-slate-500 bg-white" /> {STATUS_SHAPES[2].label}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-status-alt">
              <span className="w-3 h-3 rounded border border-dashed border-slate-500 bg-slate-50 flex items-center justify-center text-[7px] font-bold text-slate-600">A</span> {STATUS_SHAPES[3].label}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-status-confirmed">
              <CheckCircle2 size={12} className="text-slate-600" /> {STATUS_SHAPES[0].label}
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-direct"><span className="w-3 h-3 rounded-full border border-indigo-300 bg-indigo-100" /> Direct</span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-reserved-open">
              <span
                className="w-3 h-3 rounded border-2 border-dashed border-slate-400 bg-slate-50"
                style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(148,163,184,0.35) 0 2px, transparent 2px 4px)" }}
              /> WS Reserved (open)
            </span>
            <span className="inline-flex items-center gap-1.5" data-testid="legend-reserved-blocked">
              <span
                className="w-3 h-3 rounded border-2 border-dashed border-slate-400 bg-slate-100 opacity-70 line-through text-transparent"
              >—</span> WS Reserved (blocked)
            </span>
          </div>
          {viewMode === "month" ? (
            <div className="flex items-center gap-1">
              <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="clinic-prev-month"><ChevronLeft size={16} /></button>
              <button onClick={() => { const n = new Date(); setMonthAnchor(new Date(n.getFullYear(), n.getMonth(), 1)); }} className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="clinic-today">Today</button>
              <button onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="clinic-next-month"><ChevronRight size={16} /></button>
              <button onClick={load} className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid="clinic-refresh"><RefreshCw size={14} /></button>
              <div className="text-sm font-medium text-slate-700 ml-2" data-testid="clinic-month-label">{monthLabel}</div>
            </div>
          ) : (
          <div className="flex items-center gap-1">
            <button onClick={() => withPaintAutoCommit(() => setWeekStart(addDays(weekStart, -7)))} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="clinic-prev-week"><ChevronLeft size={16} /></button>
            <button onClick={() => withPaintAutoCommit(() => setWeekStart(startOfWeek(new Date())))} className="px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-lg" data-testid="clinic-today">Today</button>
            <button onClick={() => withPaintAutoCommit(() => setWeekStart(addDays(weekStart, 7)))} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="clinic-next-week"><ChevronRight size={16} /></button>
            <button onClick={load} className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid="clinic-refresh"><RefreshCw size={14} /></button>
            <div className="text-sm font-medium text-slate-700 ml-2" data-testid="clinic-week-label">{weekLabel}</div>
          </div>
          )}
        </div>
        {err && <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 inline-flex items-center gap-2"><AlertTriangle size={14} /> {err}</div>}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
        ) : viewMode === "calendar" ? (
          <>
            {spotlight && (
              <div
                data-testid="spotlight-banner"
                className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900"
              >
                <span className="min-w-0">
                  Choosing a time for <strong>{spotlight.patient_name}</strong>
                  <span data-testid="spotlight-patient-details" className="text-blue-800">
                    {" · DOB "}{fmtMDY(spotlight.details?.date_of_birth)}
                    {" · DOI "}{fmtMDY(spotlight.details?.date_of_injury)}
                    {" · "}{detailText(spotlight.details?.therapy_type)}
                    {" · "}{joinBodyParts(spotlight.details?.body_parts)}
                  </span>
                  {" — "}{spotlight.windows} proposed window{spotlight.windows === 1 ? "" : "s"}
                </span>
                {hasNextWeekWindows && (
                  <span className="text-xs text-blue-700" data-testid="spotlight-more-next-week">
                    more windows next week →
                  </span>
                )}
                {/* B.5 — which clinic this booking is for, in the hue the
                    blocks carry. Same map, same name, same dot. */}
                {spotlight.location_code && bookingTint && (
                  <span
                    data-testid="booking-location-pill"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                    style={{ backgroundColor: bookingTint.tint, color: bookingTint.text, borderColor: bookingTint.border }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: bookingTint.border }} />
                    {allLabels[spotlight.location_code] || spotlight.location_code}
                  </span>
                )}
                {/* A.2 — jump chips: one per window date, in order. */}
                {jumpDates.length > 0 && (
                  <span className="inline-flex items-center gap-1" data-testid="booking-jump-chips">
                    {jumpDates.map((d) => {
                      const inWeek = d >= toISODate(weekStart) && d <= weekEndISO;
                      return (
                        <button
                          key={d}
                          type="button"
                          data-testid={`booking-jump-chip-${d}`}
                          aria-pressed={inWeek}
                          onClick={() => setWeekStart(startOfWeek(new Date(`${d}T00:00:00`)))}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                            inWeek
                              ? "bg-blue-600 text-white border-blue-700"
                              : "bg-white text-blue-700 border-blue-300 hover:border-blue-500"
                          }`}
                        >
                          {new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })}
                        </button>
                      );
                    })}
                  </span>
                )}
                {/* A.1 — the escape hatch, off by default, remembered for
                    the session. Re-revealed pending work is dimmed: it is
                    context, not the working set. */}
                <button
                  type="button"
                  data-testid="booking-show-others-toggle"
                  aria-pressed={showOtherPending}
                  onClick={toggleOtherPending}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                    showOtherPending
                      ? "bg-blue-100 text-blue-900 border-blue-400"
                      : "bg-white text-blue-700 border-blue-200 hover:border-blue-400"
                  }`}
                >
                  {showOtherPending ? "Hide other pending requests" : "Show other pending requests"}
                </button>
                <button
                  type="button"
                  data-testid="spotlight-back"
                  onClick={() => { exitSpotlight(); setViewMode("month"); }}
                  className="ml-auto text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
                >
                  Back to Month
                </button>
              </div>
            )}
            <NetworkCalendarGrid
              weekStart={weekStart}
              requests={requests}
              onBlockClick={handleBlockClick}
              showLocationCode={false}
              confirmMode={!paintMode}
              onSlotPick={setSlotPick}
              spotlightRequestId={spotlight?.id ?? null}
              dimmedRequestIds={dimmedRequestIds}
              locationColors={locationColors}
              locationLabels={locationLabels}
              locationIdentity={isMultiLocation}
              reservedSlots={visibleReserved}
              reservedMode={paintMode ? "none" : "clinic"}
              onReservedClick={handleReservedClick}
              paintMode={paintMode}
              paintedAdds={paintAdds}
              paintedRemoves={paintRemoves}
              onPaintCell={handlePaintCell}
            />
          </>
        ) : (
          <ClinicMonthView
            monthAnchor={monthAnchor}
            requests={requests}
            onOpen={setActiveRequest}
            onSpotlight={activateSpotlight}
            highlightRequestId={highlightRequestId}
            locationColors={locationColors}
            locationLabels={locationLabels}
            reservedSlots={visibleReserved}
            reservedMode="clinic"
            onReservedClick={handleReservedClick}
          />
        )}
      </div>

      <ClinicRequestDetailModal
        open={!!activeRequest && activeRequest.type !== "direct_booking"}
        request={activeRequest}
        profile={profile}
        onClose={() => setActiveRequest(null)}
        onConfirmed={onChanged}
        onSuggested={onChanged}
      />

      <DirectBookingDetailModal
        open={activeRequest?.type === "direct_booking"}
        item={activeRequest}
        onClose={() => setActiveRequest(null)}
      />

      {/* In-block slot pick → small confirm dialog ("Confirm Tue 9:30 AM?") */}
      {slotPick && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="slot-confirm-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-1">Confirm this time?</h3>
            <p className="text-sm text-slate-600" data-testid="slot-confirm-label">
              {slotPick.request.patient_name || `Patient #${slotPick.request.patient_id}`} —{" "}
              <strong>
                {new Date(`${slotPick.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}{" "}
                {fmtMin12(rangeToMin(slotPick.slot_start))}
              </strong>
            </p>
            {/* C.8 — the SAME picker as the Pending Requests modal, seeded
                at the clicked slot. One component, two entrances. */}
            <WindowTimePicker
              window={{ id: slotPick.time.id, date: slotPick.date,
                        start_time: slotPick.time.start_time,
                        end_time: slotPick.time.end_time }}
              durationMinutes={pickDuration(slotPick.request)}
              booked={[]}
              locationLabel={locationLabels?.[slotPick.request.location_code] || ""}
              value={rangeToMin(slotPick.slot_start)}
              onChange={(min) => setSlotPick((sp) => sp && { ...sp, slot_start: toHms(min) })}
            />
            <p className="mt-2 mb-4 text-xs text-slate-500" data-testid="slot-confirm-span">
              {fmt12(rangeToMin(slotPick.slot_start))} – {fmt12(rangeToMin(slotPick.slot_start) + pickDuration(slotPick.request))}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="slot-confirm-cancel"
                onClick={() => setSlotPick(null)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="slot-confirm-submit"
                onClick={confirmSlot}
                disabled={slotBusy}
                className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg inline-flex items-center gap-1.5"
              >
                {slotBusy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reserved-slot block/unblock confirm — small dialog, no PHI, no
          endpoint call until the user confirms. */}
      {reservedPending && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="reserved-confirm-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
            <h3 className="text-sm font-bold text-slate-900 mb-1">
              {reservedPending.action === "block" ? "Mark this slot as filled?" : "Reopen this slot?"}
            </h3>
            <p className="text-sm text-slate-600 mb-4" data-testid="reserved-confirm-summary">
              WS {fmtTime(reservedPending.instance.slot_time)} on{" "}
              {new Date(`${reservedPending.instance.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              {reservedPending.action === "block"
                ? " — WestStar will no longer be able to claim this slot."
                : " — WestStar can claim this slot again."}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-testid="reserved-confirm-cancel"
                onClick={() => setReservedPending(null)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="reserved-confirm-submit"
                onClick={submitReservedAction}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg ${
                  reservedPending.action === "block"
                    ? "bg-slate-700 hover:bg-slate-800"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {reservedPending.action === "block" ? "Mark as filled" : "Reopen slot"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
